import React, { createContext, useContext, useState, useEffect } from 'react';
import { useBusiness } from './BusinessContext';
import { useAuth } from './AuthContext';
import { useClientManagement } from '../hooks/useClientManagement';
import { useActivityLog } from '../hooks/useActivityLog';
import { useActivityParser } from '../hooks/useActivityParser';
import { useReferralManagement } from '../hooks/useReferralManagement';
import { useBusinessAnalytics } from '../hooks/useBusinessAnalytics';
import { 
  BusinessAgentContextType, 
  ClientActivity,
  ClientHome
} from '../types/business';
import { AgentMessage } from '../types/chat';
import { ACTIVITY_TYPE_LABELS } from '../../shared/constants';

// Create context with default undefined value
const BusinessAgentContext = createContext<BusinessAgentContextType | undefined>(undefined);

export function BusinessAgentProvider({ children }: { children: React.ReactNode }) {
  const { business } = useBusiness();
  const { user } = useAuth();
  
  // Agent verification state
  const [isAgent, setIsAgent] = useState<boolean>(false);
  const [agentError, setAgentError] = useState<string | null>(null);
  
  // Client management using the consolidated hook
  const {
    clients,
    totalClients,
    activeClients,
    isLoadingClients,
    clientsError,
    clientHomes,
    isLoadingClientHomes,
    clientHomesError,
    addClient: addClientOperation,
    updateClient: updateClientOperation,
    deleteClient: deleteClientOperation,
    fetchClientHomes,
    isAddingClient,
    isUpdatingClient,
    isDeletingClient,
    fetchClients,
    refreshClients: refreshClientsFromHook // Rename to avoid conflict
  } = useClientManagement({ 
    businessId: business?.id, 
    agentEmail: user?.email 
  });
  
  // Agent activity log state using the custom hook with agent-specific endpoint
  const {
    activities: rawActivities,
    isLoading: isLoadingActivities,
    error: activitiesError,
    pagination: activityPagination,
    refreshActivities: refreshAgentActivities,
    loadMoreActivities: loadMoreActivitiesHook,
  } = useActivityLog({ 
    initialLimit: 10,
    customEndpointPath: true
  });

  // Parse activities for display
  const { parseActivities } = useActivityParser();
  
  // Transform activities to ClientActivity format for backwards compatibility
  const recentActivity: ClientActivity[] = parseActivities(rawActivities).map(activity => ({
    id: activity.id,
    clientName: activity.user?.name || activity.agent?.name || 'Unknown',
    clientId: activity.user?.email || activity.agent?.email || activity.id,
    type: ACTIVITY_TYPE_LABELS[activity.type] || 'Unknown Activity',
    description: activity.description,
    timestamp: activity.formattedDate,
    propertyAddress: activity.homeAddress
  }));

  // Override refresh to use agent-specific endpoint
  const fetchRecentActivity = async () => {
    if (!business?.id || !isAgent || !user?.email) {
      return;
    }
    await refreshAgentActivities(`${business.id}/${encodeURIComponent(user.email)}/activity`);
  };

  // Wrapper for load more activities using agent-specific endpoint
  const loadMoreActivities = async () => {
    if (!business?.id || !isAgent || !user?.email) {
      return;
    }
    await loadMoreActivitiesHook(`${business.id}/${encodeURIComponent(user.email)}/activity`);
  };

  // Delegation limits state
  const [delegationLimit, _setDelegationLimit] = useState<number>(10); // example limit
  const [currentDelegations, setCurrentDelegations] = useState<number>(0);
  
  // Messages state
  const [messages, setMessages] = useState<AgentMessage[]>([]);
  const [unreadMessages, setUnreadMessages] = useState<number>(0);
  const [isLoadingMessages, setIsLoadingMessages] = useState<boolean>(false);
  const [messagesError, setMessagesError] = useState<string | null>(null);
  
  // Stats state
  const [monthlyStats] = useState({
    newClients: 8,
    totalAnalyses: 42,
    avgResponseTime: '2.3 hours',
    clientSatisfaction: 4.8
  });

  // Referral management using the existing hook
  const {
    referrals,
    isLoadingReferrals,
    referralsError,
    addReferral: addReferralOperation,
    updateReferral: updateReferralOperation,
    deleteReferral: deleteReferralOperation,
    refreshReferrals: refreshReferralsOperation,
    isAddingReferral,
    isUpdatingReferral,
    isDeletingReferral
  } = useReferralManagement({ 
    businessId: business?.id,
    userType: 'agent',
    userEmail: user?.email
  });

  // Business analytics hook for lazy-loaded analytics with caching
  const {
    getClientAnalytics,
    isLoadingClientAnalytics,
    clientAnalyticsError,
    getAgentAnalytics,
    isLoadingAgentAnalytics,
    agentAnalyticsError,
    clearCache: clearAnalyticsCache,
    refreshCache: refreshAnalyticsCache,
    getCacheInfo: getAnalyticsCacheInfo
  } = useBusinessAnalytics({
    businessId: business?.id,
    userEmail: user?.email,
    userType: 'agent'
  });

  // Verify agent status when component mounts or user changes
  useEffect(() => {
    if (user) {
      if (user.is_agent) {
        setIsAgent(true);
        setAgentError(null);
      } else {
        setIsAgent(false);
        setAgentError('Access denied: You are not registered as an agent');
      }
    } else {
      setIsAgent(false);
      setAgentError('User authentication required');
    }
  }, [user]);

  // Update current delegations when clients change
  useEffect(() => {
    setCurrentDelegations(clients.filter(client => client.status === 'active').length);
  }, [clients]);

  // Wrapper functions that include agent verification
  const addClient = async (clientData: {
    clientEmail: string;
    clientName?: string;
    clientPhone?: string;
    clientDetails?: string;
  }): Promise<{ success: boolean; error?: string }> => {
    if (!isAgent) {
      return { success: false, error: 'Only agents can add clients' };
    }
    const result = await addClientOperation(clientData);
    
    if (result.success) {
      // Refresh clients to get the updated list
      await fetchClients();
    }
    
    return result;
  };

  const updateClient = async (
    clientEmail: string,
    updates: {
      clientName?: string;
      clientEmail?: string;
      clientPhone?: string;
      clientDetails?: string;
      status?: 'active' | 'inactive' | 'pending';
    }
  ): Promise<{ success: boolean; error?: string }> => {
    if (!isAgent) {
      return { success: false, error: 'Only agents can update clients' };
    }

    const result = await updateClientOperation(clientEmail, updates);
    
    if (result.success) {
      // Refresh clients to get the updated list
      await fetchClients();
    }
    
    return result;
  };

  const deleteClient = async (clientEmail: string): Promise<{ success: boolean; error?: string }> => {
    if (!isAgent) {
      return { success: false, error: 'Only agents can delete clients' };
    }

    const result = await deleteClientOperation(clientEmail);
    
    if (result.success) {
      // Refresh clients to get the updated list
      await fetchClients();
    }
    
    return result;
  };

  // Helper function to get homes for a specific client
  const getClientHomes = (clientEmail: string): ClientHome[] => {
    return clientHomes[clientEmail] || [];
  };

  // Enhanced fetch client homes with agent verification
  const fetchClientHomesVerified = async (clientEmail: string): Promise<void> => {
    if (!isAgent) {
      console.warn('Only agents can fetch client homes');
      return;
    }
    return fetchClientHomes(clientEmail);
  };

  const fetchMessages = async () => {
    if (!business?.id || !isAgent) {
      return;
    }
    
    setIsLoadingMessages(true);
    setMessagesError(null);
    
    try {
      // Mock data for now - in the future this would be an API call
      const mockMessages: AgentMessage[] = [
        {
          id: '1',
          clientName: 'Sarah Johnson',
          clientId: '1',
          subject: 'Question about property valuation',
          preview: 'Hi, I had a question about the recent valuation report...',
          timestamp: '1 hour ago',
          isRead: false,
          isUrgent: false
        },
        {
          id: '2',
          clientName: 'Emily Rodriguez',
          clientId: '3',
          subject: 'Urgent: Property inspection concerns',
          preview: 'I noticed some issues during my walkthrough that I wanted to discuss...',
          timestamp: '2 days ago',
          isRead: false,
          isUrgent: true
        },
        {
          id: '3',
          clientName: 'Michael Chen',
          clientId: '2',
          subject: 'Thank you for the analysis',
          preview: 'The detailed report was very helpful. I have a few follow-up questions...',
          timestamp: '3 days ago',
          isRead: true,
          isUrgent: false
        }
      ];
      
      setMessages(mockMessages);
      setUnreadMessages(mockMessages.filter(msg => !msg.isRead).length);
    } catch (error) {
      console.error('Error fetching messages:', error);
      setMessagesError(error instanceof Error ? error.message : 'An unexpected error occurred');
      setMessages([]);
      setUnreadMessages(0);
    } finally {
      setIsLoadingMessages(false);
    }
  };

  // Auto-fetch data when business changes and user is verified as agent
  useEffect(() => {
    if (business?.id && isAgent && user?.email) {
      fetchClients();
      fetchRecentActivity();
      fetchMessages();
    }
  }, [business?.id, isAgent, user?.email, fetchClients]);

  // Refresh functions
  const refreshClients = refreshClientsFromHook;

  // Refresh activities function for external use
  const refreshActivities = async () => {
    if (business?.id && isAgent && user?.email) {
      await fetchRecentActivity();
    }
  };

  return (
    <BusinessAgentContext.Provider
      value={{
        // Access control
        isAgent,
        agentError,
        
        // Clients - all from the hook now
        clients,
        isLoadingClients,
        clientsError,
        refreshClients,
        totalClients,
        activeClients,
        addClient,
        isAddingClient,
        updateClient,
        isUpdatingClient: isUpdatingClient || isDeletingClient,
        deleteClient,
        
        // Client homes - exposed from hook with agent verification
        clientHomes,
        isLoadingClientHomes,
        clientHomesError,
        fetchClientHomes: fetchClientHomesVerified,
        getClientHomes,
        
        // Delegation limits
        delegationLimit,
        currentDelegations,
        
        // Recent Activity (backwards compatibility)
        recentActivity,
        isLoadingActivity: isLoadingActivities,
        activityError: activitiesError,
        
        // Activity Log (same parity as admin)
        activities: rawActivities,
        isLoadingActivities,
        activitiesError,
        activityPagination,
        refreshActivities,
        loadMoreActivities,
        
        // Messages
        messages,
        unreadMessages,
        isLoadingMessages,
        messagesError,
        
        // Stats
        monthlyStats,

        // Referrals (agent-specific)
        referrals,
        isLoadingReferrals,
        referralsError,
        addReferral: addReferralOperation,
        updateReferral: updateReferralOperation,
        deleteReferral: deleteReferralOperation,
        refreshReferrals: refreshReferralsOperation,
        isAddingReferral,
        isUpdatingReferral,
        isDeletingReferral,

        // Business Analytics (lazy-loaded with caching)
        getClientAnalytics,
        isLoadingClientAnalytics,
        clientAnalyticsError,
        getAgentAnalytics,
        isLoadingAgentAnalytics,
        agentAnalyticsError,
        clearAnalyticsCache,
        refreshAnalyticsCache,
        getAnalyticsCacheInfo
      }}
    >
      {children}
    </BusinessAgentContext.Provider>
  );
}

export const useBusinessAgent = () => {
  const context = useContext(BusinessAgentContext);
  if (context === undefined) {
    throw new Error('useBusinessAgent must be used within a BusinessAgentProvider');
  }
  return context;
};